import html2pdf from "html2pdf.js";
import { axiosPrivate } from "../axios/axios";
import { masterAPI } from "../Pages/Masters/dataConfig";
import { toast } from "react-toastify";
import EamLogo from "../images/EamLogo2-removebg.png";

// Fetch a url and return it as a base64 data-uri (so canvas isn't tainted).
const toDataUrl = async (url) => {
  const resp = await fetch(url, { mode: "cors" });
  const blob = await resp.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Make every <img> render in the PDF. Prefer the server image (inlined as a
// data-uri); if it can't be fetched (e.g. cross-origin on localhost) fall back
// to the bundled logo so the box is never blank.
const resolveImages = async (root) => {
  const images = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    images.map(async (img) => {
      const src = img.getAttribute("src") || "";
      try {
        img.src = await toDataUrl(src);
      } catch (e) {
        if (/logo/i.test(src) || /eamlogo/i.test(src)) {
          img.src = EamLogo;
        }
      }
    }),
  );
};

// Fetches a print html document from the given endpoint, converts it to a
// styled PDF and downloads it. `suffix` is appended to the url (e.g. a flag).
const printHtmlDocument = async (link, id, fileName, suffix) => {
  if (!id || !link) return;

  try {
    const url = suffix !== undefined ? `${link}/${id}/${suffix}` : `${link}/${id}`;
    const res = await axiosPrivate.get(url);

    // The payload may be returned directly or wrapped inside `result`.
    const data = res.data?.result ?? res.data ?? {};
    const fileContent = data.fileContent ?? data.html ?? data.content;

    if (!fileContent) {
      console.log(res.data, "print: no fileContent");
      toast.error("No printable content received");
      return;
    }

    // base64 -> html (fall back to raw string if it isn't base64)
    let html;
    try {
      html = decodeURIComponent(escape(window.atob(fileContent)));
    } catch (decodeErr) {
      html = fileContent;
    }

    // Parse the document and move the <style>/<link> rules into the <body> so
    // they travel with the element html2pdf renders (otherwise styles are lost).
    const parsed = new DOMParser().parseFromString(html, "text/html");
    parsed.head
      .querySelectorAll("style, link[rel='stylesheet']")
      .forEach((node) => parsed.body.appendChild(node));

    // Inline images (logo) so they render even when cross-origin.
    await resolveImages(parsed.body);

    await html2pdf()
      .set({
        margin: 10, // mm on all sides (top/bottom/left/right)
        filename: fileName,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        // Honour the template's CSS page-breaks and avoid cutting elements.
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      })
      .from(parsed.body)
      .save();
  } catch (e) {
    console.log("print error:", e?.message || e);
    toast.error("Failed to generate PDF");
  }
};

// Downloads the Work Order document as a PDF (named from the frontend).
export const printWorkOrder = (workOrderId, name) =>
  printHtmlDocument(
    masterAPI["workOrder"].print,
    workOrderId,
    name ? `${name}.pdf` : "WorkOrder.pdf",
  );

// Downloads the Part Reservation form as a PDF (named from the frontend).
export const printReservationForm = (workOrderId, name, flag) =>
  printHtmlDocument(
    masterAPI["partsReservation"].print,
    workOrderId,
    name ? `${name}.pdf` : "ReservationForm.pdf",
    flag,
  );
